// The docked chat's open state and width, kept per browser (Lane 1355, moved
// from the sourcing event workspace of Lane 1344 without a change in behavior).
// The buyer drags the dock's left edge or uses the arrow keys (Ryan, slice 2b:
// "a little bit more space in the chat"). Both are conveniences: a browser that
// blocks storage gets the defaults.
import { useEffect, useState } from 'react';

export const CHAT_WIDTH = { min: 320, max: 720, start: 460 };
const MAIN_MIN = 520;      // the workspace beside the chat never gets narrower than this
const NUDGE_PX = 20;

function clampWidth(w, mainMin) {
  const room = typeof window === 'undefined' ? CHAT_WIDTH.max : window.innerWidth - mainMin;
  return Math.round(Math.max(CHAT_WIDTH.min, Math.min(w, CHAT_WIDTH.max, room)));
}

function readOpen(key, defaultOpen) {
  try {
    const saved = window.localStorage.getItem(key);
    return saved === null ? defaultOpen : saved !== 'no';
  } catch (e) { return defaultOpen; }
}

function readWidth(key, mainMin) {
  try {
    const w = Number(window.localStorage.getItem(key));
    return clampWidth(Number.isFinite(w) && w > 0 ? w : CHAT_WIDTH.start, mainMin);
  } catch (e) { return clampWidth(CHAT_WIDTH.start, mainMin); }
}

function save(key, value) {
  try { window.localStorage.setItem(key, value); } catch (e) { /* a convenience only */ }
}

// `storagePrefix` keeps each workspace's saved choice apart ("srce-chat" keeps
// the keys the sourcing workspace always used, so no buyer loses a setting).
// `defaultOpen`: the state before the buyer has chosen (the deck's narrow drawer
// starts closed, so it never covers the slide on first sight).
// `reclampOnResize`: when the room changes, the dock takes the buyer's saved width as
// far as the room allows (not saved), so the workspace keeps `mainMin` (the deck; the
// sourcing screen does not ask for it).
export default function useDockedChat({ storagePrefix, mainMin = MAIN_MIN, defaultOpen = true,
  reclampOnResize = false }) {
  const openKey = `${storagePrefix}-open`;
  const widthKey = `${storagePrefix}-width`;
  const [open, setOpenState] = useState(() => readOpen(openKey, defaultOpen));
  const [width, setWidth] = useState(() => readWidth(widthKey, mainMin));

  // The app's Feedback button moves left of the docked chat while it is open.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('wchat-docked');
    document.body.style.setProperty('--wchat-w', `${width}px`);
    return () => {
      document.body.classList.remove('wchat-docked');
      document.body.style.removeProperty('--wchat-w');
    };
  }, [open, width]);

  useEffect(() => {
    if (!reclampOnResize) return undefined;
    // The buyer's saved width, as far as the room allows now (it grows back too).
    const reclamp = () => setWidth(readWidth(widthKey, mainMin));
    reclamp();   // the room itself changed (a new mainMin)
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [reclampOnResize, mainMin, widthKey]);

  // Drag the chat's left edge; the width is saved when the drag ends.
  const startDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    let last = startW;
    const move = (ev) => { last = clampWidth(startW + (startX - ev.clientX), mainMin); setWidth(last); };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.classList.remove('wchat-dragging');
      save(widthKey, String(last));
    };
    document.body.classList.add('wchat-dragging');
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const nudge = (e) => {
    const step = e.key === 'ArrowLeft' ? NUDGE_PX : e.key === 'ArrowRight' ? -NUDGE_PX : 0;
    if (!step) return;
    e.preventDefault();
    const w = clampWidth(width + step, mainMin);
    setWidth(w);
    save(widthKey, String(w));
  };

  const setOpen = (next) => {
    setOpenState(next);
    save(openKey, next ? 'yes' : 'no');
  };

  return { open, setOpen, width, startDrag, nudge };
}
